import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { fromCognitoIdentity } from "@aws-sdk/credential-provider-cognito-identity";
import {
  AdminLibraryObject,
  CreateLibraryVersionRequest,
  LibraryDraftSpaceResponse,
  LibraryVersion,
  PatchVersionRequest,
  UploadLibraryResourceResponse,
} from "services/content/model";
import { getAWSConfig } from "../config";
import { api } from "../restUtil";
import { pollWithBackoff } from "../../utils/async";

// How long to poll a background content copy before giving up.
const COPY_TIMEOUT_MS = 120_000;
const COPY_MIN_DELAY_MS = 2_000;

/* Internal sentinel: thrown to keep pollWithBackoff polling while the copy is unsettled. */
class CopyInProgressError extends Error {}

export default class LibraryVersionService {
  private bucket: string = "";
  private s3: S3Client | null = null;
  private checkCredentialsPromise: Promise<void> | null = null;

  private async refreshToken() {
    const resp = await getAWSConfig();
    const credentialsProvider = fromCognitoIdentity({
      clientConfig: { region: resp.region },
      identityId: resp.cognito.aws_cognito_identity_id,
      logins: {
        "cognito-identity.amazonaws.com": () =>
          getAWSConfig().then(r => r.cognito.aws_cognito_token),
      },
    });
    this.bucket = resp.bucket;
    this.s3 = new S3Client({
      region: resp.region,
      credentials: credentialsProvider,
    });
  }

  private async doCheckCredentials() {
    if (!this.s3) {
      await this.refreshToken();
    }
  }

  private checkCredentials() {
    if (this.checkCredentialsPromise) return this.checkCredentialsPromise;
    const promise = this.doCheckCredentials().finally(() => {
      this.checkCredentialsPromise = null;
    });
    this.checkCredentialsPromise = promise;
    return promise;
  }

  async getSignedResourceURL(key: string, resource: string) {
    await this.checkCredentials();
    return getSignedUrl(
      this.s3!,
      new GetObjectCommand({
        Bucket: this.bucket,
        Key: `${key}/assets/${resource}`,
      }),
      { expiresIn: 3600 }
    );
  }

  fetchVersion(libraryVersionId: string) {
    return api.get<LibraryVersion>(`/admin/library/version/${libraryVersionId}`);
  }

  uploadContentFile(libraryVersionId: string, xml: string) {
    return api.put<string, void>(`/admin/library/version/${libraryVersionId}/html`, xml, {
      headers: { "Content-Type": "application/xml" },
    });
  }

  fetchEditorFile(libraryVersionId?: string, libraryObjectId?: string) {
    const url = libraryVersionId
      ? `/admin/library/version/${libraryVersionId}/editor`
      : `/admin/library/${libraryObjectId}/editor`;
    return api.get<unknown>(url);
  }

  uploadEditorFile(libraryVersionId: string, editor: unknown) {
    return api.put<unknown, void>(
      `/admin/library/version/${libraryVersionId}/editor`,
      editor
    );
  }

  listObjects(libraryObjectId?: string | null, libraryVersionId?: string | null) {
    return api.get<LibraryDraftSpaceResponse>("/admin/library/versions", {
      parent_library_object_id: libraryObjectId,
      parent_library_version_id: libraryVersionId,
    });
  }

  // Create a version and resolve with the finished version. The S3 content copy runs in the
  // background, so we poll the version's content_copy_status until it settles. A blank draft
  // (no source) has a null status and is returned on the first fetch.
  async createVersion(
    libraryObjectId?: string,
    update?: CreateLibraryVersionRequest,
    // The row exists before the copy settles, so a caller that has to clean
    // up after itself needs the id even when the poll below throws.
    onCreated?: (versionId: string) => void
  ) {
    const payload: CreateLibraryVersionRequest = { ...(update ?? {}) };
    if (libraryObjectId) payload.original_id = libraryObjectId;

    const { version_id } = await api.post<
      CreateLibraryVersionRequest,
      { version_id: string }
    >("/library/admin/versions", payload);
    onCreated?.(version_id);

    const finishedVersion = await pollWithBackoff({
      fn: async () => {
        const version = await this.fetchVersion(version_id);
        if (
          version.content_copy_status === "queued" ||
          version.content_copy_status === "running"
        ) {
          throw new CopyInProgressError();
        }
        return version;
      },
      shouldRetry: error => error instanceof CopyInProgressError,
      timeout: COPY_TIMEOUT_MS,
      minDelay: COPY_MIN_DELAY_MS,
    });

    if (finishedVersion.content_copy_status === "failed") {
      throw new Error(
        finishedVersion.content_copy_error ?? "Library content copy failed."
      );
    }

    return finishedVersion;
  }

  deleteVersion(libraryVersionId: string) {
    return api.delete<void, void>(`/admin/library/version/${libraryVersionId}`);
  }

  updateVersion(libraryVersionId: string, update: PatchVersionRequest) {
    return api.patch<PatchVersionRequest, LibraryVersion>(
      `/admin/library/version/${libraryVersionId}`,
      update
    );
  }

  fetchUploadUrls(libraryVersionId: string, filesMeta: { name: string; type: string }[]) {
    const payload = {
      resources: filesMeta.map(item => ({ name: item.name, content_type: item.type })),
    };
    return api.post<typeof payload, UploadLibraryResourceResponse>(
      `/admin/library/version/${libraryVersionId}/content/resources`,
      payload
    );
  }

  uploadResource(uploadUrl: string, file: File, abortSignal?: AbortSignal) {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = true;
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", file.type);
      if (abortSignal) {
        abortSignal.addEventListener("abort", () => xhr.abort());
      }
      xhr.onreadystatechange = function () {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) resolve();
          else reject(new Error("Error uploading file."));
        }
      };
      xhr.send(file);
    });
  }

  /** Publishes a draft; the server responds with the published LibraryObject. */
  publishVersion(libraryVersionId: string) {
    return api.post<void, { id: string }>(
      `/admin/library/version/${libraryVersionId}/publish`
    );
  }

  uploadPreview(
    libraryVersionId: string,
    type: "regular" | "vertical" | "horizontal" | "icon",
    preview: File
  ) {
    return api.post<File, void>(
      `/admin/library/version/${libraryVersionId}/preview/${type}`,
      preview,
      { headers: { "Content-Type": preview.type } }
    );
  }

  fetchObject(libraryObjectId: string) {
    return api.get<AdminLibraryObject>(`/library/${libraryObjectId}`);
  }

  deleteLibraryObject(libraryObjectId: string) {
    return api.delete<void, void>(`/admin/library/${libraryObjectId}`);
  }
}
